import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { LIVE_POLL_MS, qk } from '@/lib/queryClient'
import type { ListResponse, MediaRequest, RequestFilter } from '@/lib/types'

const IDLE_POLL_MS = 60_000

export const isActive = (request: MediaRequest): boolean =>
  request.stage === 'downloading' ||
  (request.download !== null && request.download.progress < 100 && request.stage !== 'available')

/**
 * Requests refresh every 10s *only* while something is actually downloading and
 * the tab is visible — each poll is billed Worker traffic and, during an
 * outage, hammering it changes nothing.
 */
export function useRequests(filter: RequestFilter = 'all') {
  return useQuery({
    queryKey: qk.requests(filter),
    queryFn: ({ signal }) => api.requests.list({ status: filter }, signal),
    placeholderData: keepPreviousData,
    refetchIntervalInBackground: false,
    refetchInterval: (query) => {
      const data = query.state.data as ListResponse<MediaRequest> | undefined
      if (!data) return IDLE_POLL_MS
      return data.items.some(isActive) ? LIVE_POLL_MS : IDLE_POLL_MS
    },
  })
}

export function useRequest(id: number) {
  return useQuery({
    queryKey: qk.request(id),
    queryFn: ({ signal }) => api.requests.get(id, signal),
    refetchIntervalInBackground: false,
    refetchInterval: (query) => {
      const data = query.state.data as MediaRequest | undefined
      return data && isActive(data) ? LIVE_POLL_MS : false
    },
  })
}

/** Admin-only full *arr + qBittorrent queue. */
export function useDownloads() {
  return useQuery({
    queryKey: qk.downloads,
    queryFn: ({ signal }) => api.downloads.list(signal),
    refetchIntervalInBackground: false,
    refetchInterval: LIVE_POLL_MS,
  })
}

/** True when any part of the payload came from the D1 snapshot. */
export function isStaleList(data: ListResponse<MediaRequest> | undefined): boolean {
  if (!data) return false
  return data.stale === true || data.items.some((item) => item.stale)
}
